import { ElementRef, Injectable, Renderer2, RendererFactory2, ViewContainerRef } from '@angular/core';
import { filter, takeWhile } from 'rxjs/operators';
import { Action, Store } from '@ngrx/store';
import { interval } from 'rxjs';
import { CwcAlert } from '@cmx-web-components/angular';

const DEFAULT_OPTIONS: AlertOptions = {
  message: '',
  duration: 5000,
  keepOpen: false,
  variant: 'info',
};

@Injectable({
  providedIn: 'root',
})
export class ArkikAlertService {
  private _container?: ViewContainerRef;
  private renderer: Renderer2;

  constructor(
    rendererFactory: RendererFactory2,
    private readonly store: Store<Store>,
  ) {
    this.renderer = rendererFactory.createRenderer(null, null);
  }

  /**
   * Displays an alert with the specified options.
   *
   * @param options - The configuration options for the alert to be shown.
   * @returns The result of the internal `_onShow` method, which handles displaying the alert.
   */
  show = (options: AlertOptions) => this._onShow(options);

  /**
   * Hides all currently displayed alerts by clearing the alert container.
   *
   * This method removes all `cwc-alert` elements from the alert container,
   * effectively hiding any visible alerts.
   */
  hide(): void {
    this.clearAlerts();
  }

  success(
    message: string,
    duration: number = DEFAULT_OPTIONS.duration,
    keepOpen: boolean = false,
    id: string = null,
  ): void {
    const options: AlertOptions = {
      message: message,
      duration: duration,
      keepOpen: keepOpen,
      variant: 'success',
    };
    if (id) options.id = id;

    this._onShow(options);
  }

  info(
    message: string,
    duration: number = DEFAULT_OPTIONS.duration,
    keepOpen: boolean = false,
    id: string = null,
  ): void {
    const options: AlertOptions = {
      message: message,
      duration: duration,
      keepOpen: keepOpen,
      variant: 'info',
    };
    if (id) options.id = id;

    this._onShow(options);
  }

  error(
    message: string,
    duration: number = DEFAULT_OPTIONS.duration,
    keepOpen: boolean = false,
    id: string = null,
  ): void {
    const options: AlertOptions = {
      message: message,
      duration: duration,
      keepOpen: keepOpen,
      variant: 'error',
    };
    if (id) options.id = id;

    this._onShow(options);
  }

  warning(
    message: string,
    duration: number = DEFAULT_OPTIONS.duration,
    keepOpen: boolean = false,
    id: string = null,
  ): void {
    const options: AlertOptions = {
      message: message,
      duration: duration,
      keepOpen: keepOpen,
      variant: 'warning',
    };
    if (id) options.id = id;

    this._onShow(options);
  }

  /**
   * Sets the container where alerts will be displayed.
   *
   * @param container - The `ViewContainerRef` instance that will serve as the host for alert components.
   */
  setContainer(container: ViewContainerRef): void {
    this._container = container;
  }

  /**
   * Removes all alert components from the alert container.
   *
   * This method queries the current alert container for all `cwc-alert` elements
   * and removes them from the DOM using the Renderer2 API.
   */
  clearAlerts(): void {
    if (this._container) {
      const hostElem = this._container.element.nativeElement;
      const alerts = hostElem.querySelectorAll('cwc-alert');
      alerts.forEach((alert: Element) => {
        this.renderer.removeChild(hostElem, alert);
      });
    }
  }

  /**
   * Removes an alert element from the DOM by its unique ID.
   *
   * This method first attempts to locate the alert element within the container using the provided ID.
   * If found, it sets the `open` property of the alert to `false` to trigger its closing animation or state.
   * After a delay of 1000 milliseconds (1 second), the alert element is removed from the DOM using the renderer.
   *
   * @param id - The unique identifier of the alert element to be removed.
   */
  removeAlertById(id: string): void {
    if (this._container) {
      const hostElem = this._container.element.nativeElement;
      const alert = hostElem.querySelector(`#${id}`);
      if (alert) {
        alert.show = false; // Close the alert first
        // Wait for the alert to close before removing it
        // This is necessary to allow any closing animations to complete
        setTimeout(() => {
          this.renderer.removeChild(hostElem, alert);
        }, 1000);
      }
    }
  }

  /**
   * Displays an alert or multiple alerts in the configured container.
   *
   * @param options - An `AlertOptions` object or an array of `AlertOptions` to display.
   *
   * @remarks
   * - Waits for the alert container (`_container`) to be available before rendering.
   * - Creates a `cwc-alert` element for each alert and sets its properties based on the provided options.
   * - Adds listeners for alert actions and close events.
   * - Appends the alert element(s) to the alert container in the DOM.
   */
  private _onShow(options: AlertOptions | AlertOptions[]): void {
    const interval$ = interval(300).pipe(
      filter(() => this._container !== undefined),
      takeWhile(() => !this._container, true),
    );
    interval$.subscribe(() => {
      const element = this.renderer.createElement('cwc-alert');
      let alert: Element;
      let id: string;
      if (Array.isArray(options)) {
        return;
      } else {
        element.message = options.message;
        element.variant = options.variant || 'info';
        id = 'cwc-alert-' + Math.random().toString(36).substring(2, 15);
        element.id = options?.id ? options.id : id;
        element.duration = options.duration || DEFAULT_OPTIONS.duration;
        element.keepOpen = options.keepOpen || false;
        element.ctaMessage = options.ctaMessage || '';
        element.hideCloseBtn = options.hideCloseBtn || false;
        const hostElem = this._container.element.nativeElement;
        alert = hostElem.querySelector(`#${element.id}`);
        if (!alert) {
          this._addAlertListener(element, options);
          const hostElem = this._container.element.nativeElement;
          this.renderer.appendChild(hostElem, element);
        }
      }
    });
  }

  /**
   * Adds event listeners to the alert element for handling CTA actions and close events.
   *
   * @param element - The alert DOM element (`cwc-alert`) to attach listeners to.
   * @param options - The alert options, including the CTA action to dispatch.
   *
   * @remarks
   * - Listens for the `cwcAlertClick` event to dispatch the CTA action via the NgRx store and hides the alert.
   * - Listens for the `cwcShow` event to detect when the alert is closed, then removes it from the DOM after a delay.
   */
  private _addAlertListener(element: ElementRef, options: AlertOptions): void {
    this.renderer.listen(element, 'cwcAlertClick', (event) => {
      this.store.dispatch(options.ctaAction || { type: 'NO_ACTION' });
      (element as unknown as CwcAlert).show = false; // Hide the alert after action
    });
    this.renderer.listen(element, 'cwcShow', (event) => {
      const isShow = event.detail;
      if (!isShow) {
        setTimeout(() => {
          const hostElem = this._container.element.nativeElement;
          this.renderer.removeChild(hostElem, element);
        }, 1000);
      }
    });
  }
}

export class AlertOptions {
  message: string;
  variant?: AlertVariant;
  keepOpen?: boolean;
  duration?: number;
  ctaMessage?: string;
  ctaAction?: Action;
  id?: string;
  hideCloseBtn?: boolean;
}

export type AlertVariant = 'error' | 'info' | 'success' | 'warning';
